import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { apiClient } from '@/services/api'
import { useAuthStore } from '@/store/authStore'
import {
  Lock, Unlock, CheckCircle, ChevronLeft, ChevronRight,
  BookOpen, Loader2, ArrowRight, Trophy
} from 'lucide-react'
import { useTerminology } from '@/hooks/useTerminology'
import type { CourseProgress, CourseProgressLesson, LessonActivity } from '@/types'

function PdfSlideViewerLocal({ activityId, lessonId, totalSlides }: {
  activityId: string
  lessonId: string
  totalSlides: number
}) {
  const [page, setPage] = useState(1)

  const slideUrl = `/api/v1/lessons/${lessonId}/activities/${activityId}/slides?page=${page}`

  return (
    <div className="flex flex-col items-center gap-3">
      <img
        key={page}
        src={slideUrl}
        alt={`Slide ${page}`}
        onContextMenu={(e) => e.preventDefault()}
        className="w-full object-contain rounded-lg select-none pointer-events-none max-w-full rounded-lg"
      />
      <div className="flex items-center gap-4">
        <button
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page <= 1}
          className="ds-btn ds-btn-secondary ds-btn-sm"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-sm text-gray-300">Slide {page} of {totalSlides}</span>
        <button
          onClick={() => setPage(p => Math.min(totalSlides, p + 1))}
          disabled={page >= totalSlides}
          className="ds-btn ds-btn-secondary ds-btn-sm"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

function ActivityRenderer({ activity, lessonId, courseCode, onComplete }: {
  activity: LessonActivity
  lessonId: string
  courseCode: string
  onComplete: () => void
}) {
  const [response, setResponse] = useState<any>(null)
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const data = activity.activity_data as any

  if (activity.activity_type === 'google_slides') {
    const embedUrl = data?.embed_url
    return (
      <div className="bg-surface-deep rounded-xl p-6">
        <h3 className="text-lg font-semibold text-theme-text mb-4">{activity.title}</h3>
        {activity.instruction && <p className="text-theme-text-muted mb-4">{activity.instruction}</p>}
        {embedUrl ? (
          <div
            className="relative w-full rounded-lg overflow-hidden pb-[56.25%]"
            onContextMenu={(e) => e.preventDefault()}
          >
            <div className="absolute inset-0 flex items-center justify-center bg-surface-deep" style={{ zIndex: 0 }}>
              <Loader2 className="w-8 h-8 animate-spin text-theme-text-muted" />
            </div>
            <iframe
              src={embedUrl}
              className="absolute inset-0 w-full h-full"
              style={{ zIndex: 1 }}
              sandbox="allow-scripts allow-same-origin"
              allowFullScreen
              loading="lazy"
              title="Google Slides"
            />
          </div>
        ) : (
          <p className="text-theme-muted text-sm">No slides configured.</p>
        )}
        <button
          onClick={onComplete}
          className="ds-btn ds-btn-primary ds-btn-full mt-4"
        >
          Mark as Viewed
        </button>
      </div>
    )
  }

  if (activity.activity_type === 'pdf_slideshow') {
    const totalSlides = data?.total_slides || data?.slide_count || 1
    return (
      <div className="ds-card rounded-xl p-6">
        <h3 className="ds-section-title mb-4">{activity.title}</h3>
        {activity.instruction && <p className="text-[var(--color-text-muted)] mb-4">{activity.instruction}</p>}
        <PdfSlideViewerLocal
          activityId={activity.id}
          lessonId={lessonId}
          totalSlides={totalSlides}
        />
        <button
          onClick={onComplete}
          className="ds-btn ds-btn-primary ds-btn-full mt-4"
        >
          Mark as Viewed
        </button>
      </div>
    )
  }

  if (activity.activity_type === 'mcq') {
    const options = data?.options || []
    return (
      <div className="ds-card rounded-xl p-6">
        <h3 className="ds-section-title mb-2">{activity.title}</h3>
        {activity.instruction && <p className="text-[var(--color-text-muted)] mb-4">{activity.instruction}</p>}
        <div className="space-y-2">
          {options.map((opt: string, i: number) => (
            <button
              key={i}
              onClick={() => !submitted && setResponse(i)}
              className={`w-full text-left p-3 rounded-lg border transition-colors ${
                response === i
                  ? 'border-[var(--color-primary)] bg-[var(--color-primary-subtle)] text-[var(--color-text)]'
                  : 'border-gray-600 text-gray-300 hover:border-gray-400'
              } ${submitted ? 'pointer-events-none' : ''}`}
            >
              {opt}
            </button>
          ))}
        </div>
        {!submitted ? (
          <button
            onClick={async () => {
              if (response === null) return
              setSubmitting(true)
              try {
                // Submit MCQ response to backend
                await apiClient.submitResponse(lessonId, activity.id, { answer: options[response] })
                setSubmitted(true)
                onComplete()
              } catch {
                // submission failed
              }
              setSubmitting(false)
            }}
            disabled={response === null || submitting}
            className="ds-btn ds-btn-primary ds-btn-full mt-4"
          >
            {submitting ? 'Submitting...' : 'Submit Answer'}
          </button>
        ) : (
          <div className="mt-4 p-3 rounded-lg bg-green-900/30 text-green-400 flex items-center gap-2">
            <CheckCircle className="w-4 h-4" /> Answer submitted
          </div>
        )}
      </div>
    )
  }

  // Fallback for other activity types
  return (
    <div className="ds-card rounded-xl p-6">
      <h3 className="ds-section-title mb-2">{activity.title}</h3>
      {activity.instruction && <p className="text-[var(--color-text-muted)] mb-4">{activity.instruction}</p>}
      <p className="text-[var(--color-text-muted)] text-sm">Activity type: {activity.activity_type}</p>
      <button
        onClick={onComplete}
        className="ds-btn ds-btn-primary ds-btn-full mt-4"
      >
        Mark as Complete
      </button>
    </div>
  )
}

export default function SelfPacedCourse() {
  const { courseCode } = useParams<{ courseCode: string }>()
  const navigate = useNavigate()
  const user = useAuthStore(state => state.user)
  const t = useTerminology()

  const [progress, setProgress] = useState<CourseProgress | null>(null)
  const [activities, setActivities] = useState<LessonActivity[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [completedActivities, setCompletedActivities] = useState<Set<string>>(new Set())
  const [advancing, setAdvancing] = useState(false)
  const [courseName, setCourseName] = useState('')

  useEffect(() => {
    if (!courseCode) return
    loadProgress()
  }, [courseCode])

  const loadProgress = async () => {
    try {
      setLoading(true)
      const res = await apiClient.getCourseProgress(courseCode!)
      if (res.success && res.data) {
        setProgress(res.data)

        // Load activities for the current or most recent lesson
        // Even completed courses can be revisited by logged-in users
        if (res.data.current_lesson) {
          await loadActivities(res.data.current_lesson.id)
        } else if (res.data.enrollment.progress_status === 'completed' && res.data.lessons?.length > 0) {
          // Course completed — load the last lesson so students can review
          const lastLesson = res.data.lessons[res.data.lessons.length - 1]
          if (lastLesson.id) await loadActivities(lastLesson.id)
        }
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/auth')
        return
      }
      setError(err.response?.data?.error || 'Failed to load course progress')
    } finally {
      setLoading(false)
    }
  }

  const loadActivities = async (lessonId: string) => {
    try {
      // Get course info for name, then load activities directly
      const infoRes = await apiClient.getCourseInfoPublic(courseCode!)
      if (infoRes.success && infoRes.data) {
        setCourseName(infoRes.data.course?.name || '')
        {
          const activitiesRes = await apiClient.getLessonActivities(lessonId)
          if (activitiesRes.success && activitiesRes.data) {
            setActivities(activitiesRes.data)
          }
        }
      }
    } catch {
      // Activities may fail if not accessible yet
      setActivities([])
    }
  }

  const handleActivityComplete = (activityId: string) => {
    setCompletedActivities(prev => new Set([...prev, activityId]))
  }

  const allActivitiesComplete = activities.length > 0 && activities.every(a => completedActivities.has(a.id))

  const handleAdvance = async () => {
    if (!courseCode) return
    setAdvancing(true)
    try {
      const res = await apiClient.advanceCourse(courseCode)
      if (res.success) {
        setCompletedActivities(new Set())
        await loadProgress()
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to advance')
    } finally {
      setAdvancing(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-bg)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[var(--color-primary)] animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--color-bg)] flex items-center justify-center p-4">
        <div className="ds-card rounded-xl p-8 max-w-md text-center">
          <h2 className="ds-section-title mb-2">Error</h2>
          <p className="text-[var(--color-text-muted)] mb-4">{error}</p>
          <button onClick={() => navigate('/')} className="ds-btn ds-btn-primary">
            Go Home
          </button>
        </div>
      </div>
    )
  }

  if (!progress) return null

  const currentLesson = progress.current_lesson

  return (
    <div className="min-h-screen bg-[var(--color-bg)] flex">
      {/* Sidebar: Lesson list */}
      <div className="w-72 bg-[var(--color-surface)] border-r border-[var(--color-border)] flex flex-col overflow-y-auto">
        <div className="p-4 border-b border-[var(--color-border)]">
          <h2 className="ds-section-title truncate">{courseName || t.course.title}</h2>
          <p className="text-sm text-[var(--color-text-muted)] mt-1">
            {t.lesson.title} {progress.enrollment.current_lesson_position} of {progress.lessons.length}
          </p>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {progress.lessons.map((lesson) => (
            <div
              key={lesson.position}
              className={`flex items-center gap-3 p-3 rounded-lg mb-1 ${
                lesson.current
                  ? 'bg-[var(--color-primary-subtle)] border border-[var(--color-primary)]'
                  : lesson.completed
                  ? 'bg-[var(--color-primary-subtle)]'
                  : 'opacity-60'
              }`}
            >
              <div className="flex-shrink-0">
                {lesson.completed ? (
                  <CheckCircle className="w-5 h-5 text-[var(--color-primary)]" />
                ) : lesson.unlocked ? (
                  <Unlock className="w-5 h-5 text-[var(--color-secondary)]" />
                ) : (
                  <Lock className="w-5 h-5 text-[var(--color-text-muted)]" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${
                  lesson.current ? 'text-[var(--color-text)]' : lesson.completed ? 'text-[var(--color-primary)]' : 'text-[var(--color-text-muted)]'
                }`}>
                  {lesson.name}
                </p>
                <p className="text-xs text-[var(--color-text-muted)]">{lesson.activity_count} activities</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto p-6">
          {currentLesson ? (
            <>
              <div className="mb-6">
                <h1 className="ds-page-title">{currentLesson.name}</h1>
                <div className="flex items-center gap-2 mt-2 text-[var(--color-text-muted)] text-sm">
                  <BookOpen className="w-4 h-4" />
                  <span>{activities.length} activities</span>
                  <span className="text-[var(--color-border)]">|</span>
                  <span>{completedActivities.size} completed</span>
                </div>
              </div>

              {/* Activities */}
              <div className="space-y-6">
                {activities.map((activity) => (
                  <motion.div
                    key={activity.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {completedActivities.has(activity.id) ? (
                      <div className="ds-card rounded-xl p-4">
                        <div className="flex items-center gap-2 text-green-400">
                          <CheckCircle className="w-5 h-5" />
                          <span className="font-medium">{activity.title}</span>
                          <span className="text-xs text-green-600 ml-auto">Completed</span>
                        </div>
                      </div>
                    ) : (
                      <ActivityRenderer
                        activity={activity}
                        lessonId={currentLesson.id}
                        courseCode={courseCode!}
                        onComplete={() => handleActivityComplete(activity.id)}
                      />
                    )}
                  </motion.div>
                ))}
              </div>

              {/* Continue button */}
              {allActivitiesComplete && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-8"
                >
                  <button
                    onClick={handleAdvance}
                    disabled={advancing}
                    className="ds-btn ds-btn-primary ds-btn-full"
                  >
                    {advancing ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        {`Continue to Next ${t.lesson.title}`} <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </motion.div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <Trophy className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
              <h2 className="ds-page-title mb-2">{`${t.course.title} Complete!`}</h2>
              <button
                onClick={() => navigate(`/course/${courseCode}/complete`)}
                className="ds-btn ds-btn-primary mt-4"
              >
                View Completion Report
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
